import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useDevice } from '../../contexts/DeviceContext';
import { buildPlaylistActivationUrl } from '../../utils/activationUrl';
import spinnerGif from '../../assets/spinner-tv.gif';
import './Boot.css';

const POLL_INTERVAL_MS = 5000;

const isBackKey = (e: KeyboardEvent): boolean => {
  var k = e.key || '';
  var code = (e as any).keyCode || (e as any).which || 0;
  if (k === 'Escape' || k === 'Esc') return true;
  if (k === 'Back' || k === 'BrowserBack' || k === 'GoBack') return true;
  if (code === 10009 || code === 461) return true;
  return false;
};

export interface BootProps {
  /** Called when Escape/Back is pressed so the caller can show the exit modal */
  onRequestExit: () => void;
  /** True while licenseStatus=active but authData is still null (profile fetch in flight) */
  loadingProfile?: boolean;
  /** True when the profile fetch failed due to a network/backend error */
  profileError?: boolean;
  /** True when the backend returned no active profile for this device */
  noActiveProfile?: boolean;
  /** Called when the user clicks "Try again" on the profile error state */
  onRetryProfile?: () => void;
}

export const Boot: React.FC<BootProps> = ({ onRequestExit, loadingProfile, profileError, noActiveProfile, onRetryProfile }) => {
  const { t } = useTranslation();
  const {
    licenseStatus,
    activationDisplayId,
    activationPassword,
    displayCode,
    devicePassword,
    playlistActivationUrl,
    refreshActivation,
  } = useDevice();

  // Back/Escape → request exit modal from parent
  const onRequestExitRef = useRef(onRequestExit);
  onRequestExitRef.current = onRequestExit;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isBackKey(e)) { e.preventDefault(); onRequestExitRef.current(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Poll every 5s while waiting for activation
  useEffect(() => {
    if (licenseStatus !== 'pending_activation' && licenseStatus !== 'expired' && licenseStatus !== 'revoked') return;
    const timer = setInterval(() => void refreshActivation(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [licenseStatus, refreshActivation]);

  // QR code URL — use displayCode (with dots) if available, else activationDisplayId
  const codeForQr = displayCode || activationDisplayId || '';
  const passForQr = devicePassword || activationPassword || '';
  const qrUrl = codeForQr
    ? buildPlaylistActivationUrl(playlistActivationUrl, codeForQr, passForQr)
    : playlistActivationUrl;

  if (loadingProfile) {
    return (
      <div className="boot boot--message">
        <img className="boot__spinner" src={spinnerGif} alt="" />
        <p className="boot__hint">{t('boot.loadingProfile')}</p>
      </div>
    );
  }

  if (noActiveProfile || profileError) {
    return (
      <div className="boot">
        <div className="boot__left">
          <div className="boot__status-banner">
            <h2 className="boot__status-title">
              {noActiveProfile ? t('boot.noActiveProfileTitle') : t('boot.profileErrorTitle')}
            </h2>
            <p className="boot__status-hint">
              {noActiveProfile ? t('boot.noActiveProfileHint') : t('boot.profileErrorHint')}
            </p>
            {profileError && onRetryProfile && (
              <button className="boot__retry-btn" onClick={onRetryProfile}>
                {t('boot.profileRetry')}
              </button>
            )}
          </div>

          <h1 className="boot__title">{t('boot.activateTitle')}</h1>
          <p className="boot__subtitle">{t('boot.activateSubtitle')}</p>

          <div className="boot__url-row">
            <span className="boot__url-label">{t('boot.activationUrl')}</span>
            <span className="boot__url-value">{playlistActivationUrl}</span>
          </div>

          <div className="boot__field">
            <span className="boot__field-label">{t('boot.deviceCodeLabel')}:</span>
            <span className="boot__field-value boot__field-value--display">
              {activationDisplayId || '…'}
            </span>
          </div>

          <div className="boot__field">
            <span className="boot__field-label">{t('boot.passwordLabel')}:</span>
            <span className="boot__field-value boot__field-value--password">
              {activationPassword || '…'}
            </span>
          </div>
        </div>

        <div className="boot__right">
          <div className="boot__qr-frame">
            {codeForQr ? (
              <QRCode value={qrUrl} size={180} level="M" bgColor="#ffffff" fgColor="#000000" />
            ) : (
              <div className="boot__qr-placeholder">QR</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (licenseStatus === 'bootstrapping') {
    return (
      <div className="boot boot--message">
        <img className="boot__spinner" src={spinnerGif} alt={t('boot.initializingAria')} />
        <p className="boot__hint">{t('boot.initializing')}</p>
      </div>
    );
  }

  // expired, revoked, pending_activation → all show the full activation screen
  const isExpired = licenseStatus === 'expired';
  const isRevoked = licenseStatus === 'revoked';

  return (
    <div className="boot">
      {/* ── Left content ───────────────────── */}
      <div className="boot__left">

        {/* Status banner for expired/revoked */}
        {(isExpired || isRevoked) && (
          <div className="boot__status-banner">
            <h2 className="boot__status-title">
              {isExpired ? t('boot.expiredTitle') : t('boot.revokedTitle')}
            </h2>
            <p className="boot__status-hint">
              {isExpired ? t('boot.expiredHint') : t('boot.revokedHint')}
            </p>
          </div>
        )}

        <h1 className="boot__title">{t('boot.activateTitle')}</h1>
        <p className="boot__subtitle">{t('boot.activateSubtitle')}</p>

        {/* Activation URL */}
        <div className="boot__url-row">
          <span className="boot__url-label">{t('boot.activationUrl')}</span>
          <span className="boot__url-value">{playlistActivationUrl}</span>
        </div>

        {/* Device code */}
        <div className="boot__field">
          <span className="boot__field-label">{t('boot.deviceCodeLabel')}:</span>
          <span className="boot__field-value boot__field-value--display">
            {activationDisplayId || '…'}
          </span>
        </div>

        {/* Password */}
        <div className="boot__field">
          <span className="boot__field-label">{t('boot.passwordLabel')}:</span>
          <span className="boot__field-value boot__field-value--password">
            {activationPassword || '…'}
          </span>
        </div>

        {/* Waiting indicator */}
        <div className="boot__waiting">
          <div className="boot__dot-pulse">
            <span /><span /><span />
          </div>
          <p className="boot__waiting-text">{t('boot.waitingActivation')}</p>
        </div>
      </div>

      {/* ── Right: QR code ─────────────────── */}
      <div className="boot__right">
        <div className="boot__qr-frame">
          {codeForQr ? (
            <QRCode value={qrUrl} size={180} level="M" bgColor="#ffffff" fgColor="#000000" />
          ) : (
            <div className="boot__qr-placeholder">QR</div>
          )}
        </div>
      </div>
    </div>
  );
};
